/**
 * 组件对象池，用于复用组件实例以减少内存分配
 */
export class ComponentPool {
    constructor(createFn, resetFn, maxSize = 1000, minSize = 10) {
        this.pool = [];
        this.stats = {
            totalCreated: 0,
            totalAcquired: 0,
            totalReleased: 0
        };
        this.createFn = createFn;
        if (resetFn) {
            this.resetFn = resetFn;
        }
        this.maxSize = maxSize;
        this.minSize = Math.max(1, minSize);
    }
    /**
     * 获取一个组件实例
     */
    acquire() {
        this.stats.totalAcquired++;
        if (this.pool.length > 0) {
            return this.pool.pop();
        }
        this.stats.totalCreated++;
        return this.createFn();
    }
    /**
     * 释放一个组件实例回池中
     */
    release(component) {
        this.stats.totalReleased++;
        if (this.pool.length >= this.maxSize) {
            return;
        }
        if (this.resetFn) {
            this.resetFn(component);
        }
        this.pool.push(component);
    }
    /**
     * 预填充对象池
     */
    prewarm(count) {
        const targetCount = Math.min(count, this.maxSize);
        for (let i = this.pool.length; i < targetCount; i++) {
            const component = this.createFn();
            if (this.resetFn) {
                this.resetFn(component);
            }
            this.pool.push(component);
            this.stats.totalCreated++;
        }
    }
    /**
     * 自动收缩池大小
     */
    shrink() {
        while (this.pool.length > this.minSize) {
            this.pool.pop();
        }
    }
    /**
     * 清空对象池
     */
    clear() {
        this.pool.length = 0;
    }
    /**
     * 获取池中可用对象数量
     */
    getAvailableCount() {
        return this.pool.length;
    }
    /**
     * 获取池的最大容量
     */
    getMaxSize() {
        return this.maxSize;
    }
    /**
     * 获取统计信息
     */
    getStats() {
        const hitRate = this.stats.totalAcquired === 0
            ? 0
            : (this.stats.totalAcquired - this.stats.totalCreated) / this.stats.totalAcquired;
        return {
            totalCreated: this.stats.totalCreated,
            totalAcquired: this.stats.totalAcquired,
            totalReleased: this.stats.totalReleased,
            hitRate: hitRate,
            currentSize: this.pool.length,
            maxSize: this.maxSize,
            minSize: this.minSize,
            utilizationRate: this.pool.length / this.maxSize
        };
    }
}
/**
 * 全局组件池管理器
 */
export class ComponentPoolManager {
    constructor() {
        this.pools = new Map();
        this.usageTracker = new Map();
        this.autoCleanupInterval = 60000;
        this.lastCleanupTime = 0;
    }
    static getInstance() {
        if (!ComponentPoolManager.instance) {
            ComponentPoolManager.instance = new ComponentPoolManager();
        }
        return ComponentPoolManager.instance;
    }
    /**
     * 注册组件池
     */
    registerPool(componentName, createFn, resetFn, maxSize, minSize) {
        this.pools.set(componentName, new ComponentPool(createFn, resetFn, maxSize, minSize));
        this.usageTracker.set(componentName, {
            createCount: 0,
            releaseCount: 0,
            lastAccessTime: Date.now()
        });
    }
    /**
     * 获取组件实例
     */
    acquireComponent(componentName) {
        const pool = this.pools.get(componentName);
        this.trackUsage(componentName, 'create');
        return pool ? pool.acquire() : null;
    }
    /**
     * 释放组件实例
     */
    releaseComponent(componentName, component) {
        const pool = this.pools.get(componentName);
        this.trackUsage(componentName, 'release');
        if (pool) {
            pool.release(component);
        }
    }
    /**
     * 追踪使用情况
     */
    trackUsage(componentName, action) {
        let tracker = this.usageTracker.get(componentName);
        if (!tracker) {
            tracker = {
                createCount: 0,
                releaseCount: 0,
                lastAccessTime: Date.now()
            };
            this.usageTracker.set(componentName, tracker);
        }
        if (action === 'create') {
            tracker.createCount++;
        }
        else {
            tracker.releaseCount++;
        }
        tracker.lastAccessTime = Date.now();
    }
    /**
     * 自动清理(定期调用)
     */
    update() {
        const now = Date.now();
        if (now - this.lastCleanupTime < this.autoCleanupInterval) {
            return;
        }
        for (const [name, tracker] of this.usageTracker.entries()) {
            const inactive = now - tracker.lastAccessTime > 120000;
            if (inactive) {
                const pool = this.pools.get(name);
                if (pool) {
                    pool.shrink();
                }
            }
        }
        this.lastCleanupTime = now;
    }
    /**
     * 获取热点组件列表
     */
    getHotComponents(threshold = 100) {
        return Array.from(this.usageTracker.entries())
            .filter(([_, tracker]) => tracker.createCount > threshold)
            .map(([name]) => name);
    }
    /**
     * 预热所有池
     */
    prewarmAll(count = 100) {
        for (const pool of this.pools.values()) {
            pool.prewarm(count);
        }
    }
    /**
     * 清空所有池
     */
    clearAll() {
        for (const pool of this.pools.values()) {
            pool.clear();
        }
    }
    /**
     * 重置管理器
     */
    reset() {
        this.pools.clear();
        this.usageTracker.clear();
    }
    /**
     * 获取全局统计信息
     */
    getGlobalStats() {
        const stats = [];
        for (const [name, pool] of this.pools.entries()) {
            stats.push({
                componentName: name,
                poolStats: pool.getStats(),
                usage: this.usageTracker.get(name)
            });
        }
        return stats;
    }
    /**
     * 获取池统计信息
     */
    getPoolStats() {
        const stats = new Map();
        for (const [name, pool] of this.pools) {
            stats.set(name, {
                available: pool.getAvailableCount(),
                maxSize: pool.getMaxSize()
            });
        }
        return stats;
    }
    /**
     * 获取池利用率信息
     */
    getPoolUtilization() {
        const utilization = new Map();
        for (const [name, pool] of this.pools) {
            const available = pool.getAvailableCount();
            const maxSize = pool.getMaxSize();
            const used = maxSize - available;
            const utilRate = maxSize > 0 ? (used / maxSize * 100) : 0;
            utilization.set(name, {
                used: used,
                total: maxSize,
                utilization: utilRate
            });
        }
        return utilization;
    }
    /**
     * 获取指定组件的池利用率
     */
    getComponentUtilization(componentName) {
        const pool = this.pools.get(componentName);
        if (!pool)
            return 0;
        const available = pool.getAvailableCount();
        const maxSize = pool.getMaxSize();
        const used = maxSize - available;
        return maxSize > 0 ? (used / maxSize * 100) : 0;
    }
}
//# sourceMappingURL=ComponentPool.js.map